import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useUITranslation } from '../../hooks';
import { useInView } from 'react-intersection-observer';
import styles from './ui-filter.module.scss';
import {
    UIAvatarProps,
    UIAvatar,
    UIButton,
    UIErrorState,
    UIIcon,
    UIIconName,
    UILoadingState,
    UIMenu,
    UIMenuItem,
    UIPopover,
    UIPopoverPlacement,
    UIPropoverRef,
    UISearchInput,
    UISubAvatarProps,
    UIText
} from '..';
import { UISpinner } from '../..';
import { UIFilterHeader } from './ui-filter-header';
import { charcoal } from '../../styles';

export type UIPaginatedFilterOption = {
    id: string;
    text: string;
    image?: string;
    subAvatar?: UISubAvatarProps;
};

type UIPaginatedFilterItemProps = {
    noneSelectedText: string;
    emptyItemsText?: string;
    errorText?: string;
    // Trigger label when items are selected but none of their names are known
    // yet (e.g. selection restored from the URL before any options load).
    selectedCountText?: (count: number) => string;
    icon?: UIIconName;
    popoverPlacement?: UIPopoverPlacement;
    // The loaded pages, in server order.
    items: UIPaginatedFilterOption[];
    // The applied selection — the component only stages changes locally and
    // reports them via onApply.
    selectedIds: string[];
    search: string;
    onSearchChange: (query: string) => void;
    isLoading: boolean;
    isError: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    onLoadMore: () => void;
    onOpenChanged?: (open: boolean) => void;
    onApply: (selectedIds: string[]) => void;
    onRetry?: () => void;
};

/**
 * The server-paginated counterpart of the filter dropdowns: options arrive in
 * pages (with server-side search), so the component never has the full list.
 * Selection is therefore id-based, and every option ever seen is cached so
 * selected rows keep their labels when the current page window no longer
 * contains them.
 */
export function UIPaginatedFilterItem(
    props: UIPaginatedFilterItemProps
): ReactElement {
    const { t } = useUITranslation();
    const {
        noneSelectedText,
        emptyItemsText,
        errorText = "Couldn't load options",
        selectedCountText = (count) => `${count} selected`,
        icon,
        popoverPlacement,
        items,
        selectedIds,
        search,
        onSearchChange,
        isLoading,
        isError,
        isFetchingNextPage,
        hasNextPage,
        onLoadMore,
        onOpenChanged,
        onApply,
        onRetry
    } = props;

    const ref = useRef<UIPropoverRef>(null);

    // Every option ever received, so off-window selections keep their meta.
    const metaRef = useRef(new Map<string, UIPaginatedFilterOption>());
    useEffect(() => {
        items.forEach((item) => {
            metaRef.current.set(item.id, item);
        });
    }, [items]);

    const itemsById = useMemo(() => {
        return new Map(items.map((item) => [item.id, item]));
    }, [items]);

    const [pendingIds, setPendingIds] = useState<Set<string>>(
        new Set(selectedIds)
    );
    const isOpenRef = useRef(false);

    // While closed, mirror the applied selection (e.g. a team change resets
    // the applied user/device selections from outside).
    useEffect(() => {
        if (!isOpenRef.current) {
            setPendingIds(new Set(selectedIds));
        }
    }, [selectedIds]);

    function onPopoverOpened() {
        isOpenRef.current = true;
        setPendingIds(new Set(selectedIds));
        onOpenChanged?.(true);
    }

    function onPopoverClosed() {
        isOpenRef.current = false;
        setPendingIds(new Set(selectedIds));
        onSearchChange('');
        onOpenChanged?.(false);
    }

    const hasSelectionChanged = useMemo(() => {
        if (pendingIds.size !== selectedIds.length) {
            return true;
        }
        return selectedIds.some((id) => !pendingIds.has(id));
    }, [pendingIds, selectedIds]);

    // Applied-selected rows are pinned to the top (as the old in-memory filter
    // sorted selected first); rows toggled while open stay in place so the
    // list doesn't jump around underneath the user.
    const listItems = useMemo(() => {
        const pinned: UIPaginatedFilterOption[] = [];
        selectedIds.forEach((id) => {
            const item = itemsById.get(id) ?? metaRef.current.get(id);
            if (item) {
                pinned.push(item);
            }
        });
        pinned.sort((a, b) => a.text.localeCompare(b.text));
        const pinnedIds = new Set(pinned.map((item) => item.id));
        return [...pinned, ...items.filter((item) => !pinnedIds.has(item.id))];
    }, [selectedIds, itemsById, items]);

    function onCheckboxChange(checked: boolean, id: string) {
        setPendingIds((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    }

    function onApplyPress() {
        ref.current?.close();
        onApply([...pendingIds]);
    }

    function onCancelPress() {
        ref.current?.close();
    }

    function onClearPress() {
        setPendingIds(new Set());
        onSearchChange('');
    }

    // Load the next page when the bottom sentinel scrolls into view.
    const { ref: bottomRef, inView } = useInView({ threshold: 0 });
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            onLoadMore();
        }
    }, [inView, hasNextPage, isFetchingNextPage, onLoadMore]);

    const selectedCount = selectedIds.length;
    const displayItem = useMemo(() => {
        for (const id of selectedIds) {
            const item = itemsById.get(id) ?? metaRef.current.get(id);
            if (item) {
                return item;
            }
        }
        return null;
    }, [selectedIds, itemsById]);

    const iconNode = useMemo(() => {
        if (!displayItem || !displayItem.image) {
            if (icon) {
                return <UIIcon name={icon} size={18} color={charcoal} />;
            } else {
                return undefined;
            }
        } else {
            return (
                <UIAvatar
                    img={displayItem.image}
                    alt={displayItem.text}
                    subAvatar={displayItem.subAvatar}
                    size={24}
                />
            );
        }
    }, [icon, displayItem]);

    const text = useMemo(() => {
        if (selectedCount === 0) {
            return noneSelectedText;
        }
        if (displayItem) {
            return displayItem.text;
        }
        return selectedCountText(selectedCount);
    }, [selectedCount, displayItem, noneSelectedText, selectedCountText]);

    const pillText =
        selectedCount > 1 && displayItem ? `+${selectedCount}` : undefined;

    const showEmpty =
        !isLoading && !isError && listItems.length === 0 && !hasNextPage;

    return (
        <UIPopover
            ref={ref}
            popoverPadding={8}
            width={300}
            placement={popoverPlacement}
            triggerNode={
                <button className={styles.buttonContainer}>
                    <UIFilterHeader
                        text={text}
                        icon={iconNode}
                        pillText={pillText}
                    />
                </button>
            }
            onClickOutside={() => {
                ref.current?.close();
            }}
        >
            <MountSensor
                onMount={onPopoverOpened}
                onUnmount={onPopoverClosed}
            />
            <UIMenu
                scroll
                maxHeight={400}
                header={
                    <UISearchInput
                        value={search}
                        placeholder={t('shared.labels.search')}
                        onChange={onSearchChange}
                    />
                }
                footer={
                    <div
                        style={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'row'
                        }}
                    >
                        <UIButton size="small" onClick={onClearPress}>
                            {t('shared.labels.clear')}
                        </UIButton>

                        {hasSelectionChanged && (
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'row',
                                    justifyContent: 'flex-end',
                                    gap: 4,
                                    zIndex: 999 // Fixes bug where checkbox behind it is clicked instead
                                }}
                            >
                                <UIButton
                                    variant="textDefault"
                                    size="small"
                                    onClick={onCancelPress}
                                >
                                    {t('shared.labels.cancel')}
                                </UIButton>
                                <UIButton
                                    variant="primary"
                                    size="small"
                                    onClick={onApplyPress}
                                >
                                    {t('shared.labels.apply')}
                                </UIButton>
                            </div>
                        )}
                    </div>
                }
            >
                {isLoading && (
                    <div style={{ padding: 16 }}>
                        <UILoadingState />
                    </div>
                )}
                {!isLoading && isError && (
                    <div style={{ padding: 16 }}>
                        <UIErrorState title={errorText} onRetry={onRetry} />
                    </div>
                )}
                {showEmpty && (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 44
                        }}
                    >
                        <UIText>{emptyItemsText}</UIText>
                    </div>
                )}
                {!isLoading &&
                    !isError &&
                    listItems.map((item) => {
                        let leftAvatarProps: UIAvatarProps | undefined =
                            undefined;
                        if (item.image) {
                            leftAvatarProps = {
                                img: item.image,
                                alt: item.text,
                                size: 24,
                                subAvatar: item.subAvatar
                            };
                        }
                        return (
                            <UIMenuItem
                                key={item.id}
                                text={item.text}
                                leftAvatarProps={leftAvatarProps}
                                checkboxProps={{
                                    checked: pendingIds.has(item.id),
                                    onChange: (checked) => {
                                        onCheckboxChange(checked, item.id);
                                    }
                                }}
                                onClick={() => {
                                    onCheckboxChange(
                                        !pendingIds.has(item.id),
                                        item.id
                                    );
                                }}
                            />
                        );
                    })}
                {!isLoading && !isError && hasNextPage && (
                    <div
                        ref={bottomRef}
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: 8
                        }}
                    >
                        {isFetchingNextPage && <UISpinner color={charcoal} />}
                    </div>
                )}
            </UIMenu>
        </UIPopover>
    );
}

/**
 * UIPopover mounts its content only while open and overrides the trigger's
 * onClick, so open/close detection has to come from the content's lifecycle.
 */
function MountSensor(props: {
    onMount: () => void;
    onUnmount: () => void;
}): null {
    const { onMount, onUnmount } = props;

    const onMountRef = useRef(onMount);
    onMountRef.current = onMount;
    const onUnmountRef = useRef(onUnmount);
    onUnmountRef.current = onUnmount;

    useEffect(() => {
        onMountRef.current();
        return () => {
            onUnmountRef.current();
        };
    }, []);

    return null;
}
